import React, { Component } from 'react';
import { connect } from 'react-redux';
import numeral from 'numeral';
import './BuildingDetails.css';
import {
  getCurrentBuilding,
  getCurrentPage,
  getWorkOrders,
  getProjects,
} from 'selectors/admin/buildings/buildings';
import {
  editBuilding,
  loadBuildingDetailsRequest,
  loadWorkOrdersRequest,
  loadProjectsRequest,
} from 'actions/admin/buildings/buildings';
import RouteTitle from 'components/shared/route-title/RouteTitle';
import DetailsHeader from 'components/shared/details/DetailsHeader';
import DetailsInfo from 'components/shared/details/DetailsInfo';
import DetailsInfoItem from 'components/shared/details/DetailsInfoItem';
import faInfoCircle from '@fortawesome/fontawesome-free-solid/faInfoCircle';
import BuildingDocumentsViewer from 'components/shared/document/BuildingDocumentsViewer';
import NavigationBar from 'components/shared/navigation/NavigationBar';
import { goToWorkOrder } from 'actions/admin/work-orders/work-orders';
import Number from 'components/shared/text/Number';
import { simpleScrollToTop } from 'services/utils/scroller-util';
import { formatPhoneNumber } from 'services/utils/text-util';
import { ROLES, TYPES, usersUtil } from 'services/utils/users-util';
import { getCurrentUser } from 'selectors/authentication';
import BuildingDetailsWorkOrders from 'components/admin/buildings/BuildingDetailsWorkOrders';
import BuildingDetailsProjects from 'components/admin/buildings/BuildingDetailsProjects';

class BuildingDetails extends Component {
  constructor(props) {
    super(props);

    // Keep track of id to avoid showing an "old" building while loading the current one
    this.buildingId = numeral(this.props.match.params.id).value();
    this.userIsAdmin = usersUtil.hasRole(
      props.currentUser,
      ROLES.SPACKLE_ADMIN
    );
    this.userIsGC = usersUtil.hasType(
      props.currentUser,
      TYPES.GENERAL_CONTRACTOR
    );
    this.currentNav = this.userIsGC ? 2 : 1;
  }

  componentDidMount() {
    if (
      this.props.currentBuilding === null ||
      this.props.currentBuilding.id !== this.buildingId
    ) {
      this.props.dispatch(
        loadBuildingDetailsRequest(
          this.buildingId,
          this.userIsAdmin || this.userIsGC
        )
      );
    }
  }

  handleWorkOrderSelection = (index) => {
    this.props.dispatch(
      goToWorkOrder(
        this.props.workOrders.content[index].id,
        this.props.workOrders.content[index].status
      )
    );
  };

  handleEdit = () => {
    simpleScrollToTop();
    this.props.dispatch(editBuilding(this.props.currentBuilding.id));
  };

  handleNavSelect = (value) => {
    this.currentNav = value;
    this.handleChange(1);
  };

  handlePageChange = (newPage) => {
    this.handleChange(newPage);
  };

  handleChange(page) {
    switch (this.currentNav) {
      case 1:
        this.props.dispatch(
          loadWorkOrdersRequest(this.props.currentBuilding.id, page)
        );
        break;

      case 2:
        this.props.dispatch(
          loadProjectsRequest(this.props.currentBuilding.id, page)
        );
        break;

      default:
    }
  }

  render() {
    const { currentBuilding } = this.props;
    return (
      <div className="building-details">
        {currentBuilding !== null && currentBuilding.id === this.buildingId && (
          <React.Fragment>
            <RouteTitle
              titles={['BUILDINGS'].concat(
                currentBuilding.name !== null && currentBuilding.name !== ''
                  ? currentBuilding.name.toUpperCase()
                  : []
              )}
            />

            <DetailsHeader
              leftHeader={{
                title: 'BUILDING',
                value:
                  currentBuilding.name !== null && currentBuilding.name !== ''
                    ? currentBuilding.name
                    : currentBuilding.location.street_address_1,
              }}
              rightHeaders={[
                [
                  {
                    title: 'IN PROGRESS',
                    value: currentBuilding.wo_in_progress_count,
                  },
                  {
                    title: 'COMPLETED',
                    value: currentBuilding.wo_complete_count,
                  },
                  {
                    title: 'TOTAL SPENT',
                    value: (
                      <Number
                        value={currentBuilding.total_spent}
                        format="$0,0"
                      />
                    ),
                  },
                ],
              ].filter((header) => !this.userIsGC)}
            />

            <div className="details-info-container">
              <DetailsInfo
                itemsContainerSize={9}
                itemsLines={[
                  [
                    <DetailsInfoItem
                      lines={[
                        currentBuilding.location.street_address_1,
                        `${currentBuilding.location.city}, ${currentBuilding.location.state} ${currentBuilding.location.zip}`,
                      ]}
                      title="ADDRESS"
                    />,
                    <DetailsInfoItem
                      lines={[
                        currentBuilding.property_owner_name,
                        currentBuilding.property_owner_address &&
                          currentBuilding.property_owner_address
                            .street_address_1,
                        currentBuilding.property_owner_address &&
                          currentBuilding.property_owner_address
                            .street_address_2,
                        currentBuilding.property_owner_address &&
                          `${currentBuilding.property_owner_address.city}` +
                            `, ${currentBuilding.property_owner_address.state}` +
                            `, ${currentBuilding.property_owner_address.zip}`,
                      ]}
                      title="PROPERTY OWNER"
                    />,
                    <DetailsInfoItem
                      lines={
                        currentBuilding.property_manager !== null
                          ? [
                              <span>
                                {`${currentBuilding.property_manager.first_name} ${currentBuilding.property_manager.last_name}`}
                              </span>,
                              <span>
                                {currentBuilding.property_manager.email}
                              </span>,
                              <span>
                                {formatPhoneNumber(
                                  currentBuilding.property_manager.phone_number
                                )}
                              </span>,
                            ]
                          : []
                      }
                      title="PROPERTY MANAGER"
                    />,
                    <DetailsInfoItem
                      lines={
                        currentBuilding.engineer !== null
                          ? [
                              <span>
                                {`${currentBuilding.engineer.first_name} ${currentBuilding.engineer.last_name}`}
                              </span>,
                              <span>{currentBuilding.engineer.email}</span>,
                              <span>
                                {formatPhoneNumber(
                                  currentBuilding.engineer.phone_number
                                )}
                              </span>,
                            ]
                          : []
                      }
                      title="BUILDING ENGINEER"
                    />,
                  ],
                  [
                    <DetailsInfoItem
                      lines={currentBuilding.unions.map((element) => {
                        return <span>{element.label}</span>;
                      })}
                      title="UNION REQUIREMENTS"
                    />,
                    <DetailsInfoItem
                      icon={faInfoCircle}
                      lines={[
                        currentBuilding.parking_on_site
                          ? 'Yes (Validated)'
                          : 'No',
                      ]}
                      title="PARKING ON SITE"
                    />,
                  ],
                ]}
                rightContainerSize={3}
                rightContainer={
                  <div className="buildings-documents-container">
                    <BuildingDocumentsViewer
                      documents={currentBuilding.documents}
                    />
                  </div>
                }
              />
              {(this.userIsAdmin ||
                this.userIsGC ||
                usersUtil.hasType(this.props.currentUser, TYPES.CLIENT)) && (
                <div className="edit-button-container">
                  <input
                    type="button"
                    className="btn button button-gray"
                    value="Edit"
                    onClick={this.handleEdit}
                  />
                </div>
              )}
            </div>

            <div className="content-container">
              <React.Fragment>
                <NavigationBar
                  items={[
                    {
                      id: 1,
                      title: 'Work Orders',
                      show: !this.userIsGC,
                    },
                    {
                      id: 2,
                      title: 'Projects',
                    },
                  ]}
                  selectedValue={this.currentNav}
                  onSelected={this.handleNavSelect}
                />

                <div className="navigation-content">
                  {this.currentNav === 1 && <BuildingDetailsWorkOrders />}
                  {this.currentNav === 2 && <BuildingDetailsProjects />}
                </div>
              </React.Fragment>
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default connect((state) => {
  return {
    currentBuilding: getCurrentBuilding(state),
    currentPage: getCurrentPage(state),
    currentUser: getCurrentUser(state),
    workOrders: getWorkOrders(state),
    prjects: getProjects(state),
  };
})(BuildingDetails);



// WEBPACK FOOTER //
// ./src/components/admin/buildings/BuildingDetails.js